import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { instantPageService, InstantPageAnalysisResult } from '../lib/instantPageService';
import { PageSpeedResult } from '../lib/types';
import { formatTimeFromMs, convertScoreToPercentage, formatDateToFrench } from '../utils/performanceUtils';
import { isDemoToken } from '../utils/demoUtils';

export interface ScorePageData {
  // Données de base du prospect
  overallScore: number;
  siteUrl: string;
  prospectEmail?: string;
  prospectId?: string;
  quizCompleted?: boolean; // Indicateur si quiz complété

  // Données d'analyse technique
  instantPageAnalysis: InstantPageAnalysisResult | null;
  pagespeedResults: PageSpeedResult | null;

  // Données formatées pour l'affichage
  performanceMetrics: {
    globalScore: number;
    performanceScore: number;
    seoScore: number;
    uxScore: number;
    technicalScore: number;
    loadingTime: string;
    firstContentfulPaint: string;
    largestContentfulPaint: string;
    criticalIssues: number;
    warnings: number;
    analysisDate: string;
  };

  // États de chargement
  loading: boolean;
  error: string | null;
}

export function useScorePageData(token?: string) {
  const [data, setData] = useState<ScorePageData>({
    overallScore: 0,
    siteUrl: '',
    prospectEmail: undefined,
    prospectId: undefined,
    quizCompleted: false,
    instantPageAnalysis: null,
    pagespeedResults: null,
    performanceMetrics: {
      globalScore: 0,
      performanceScore: 0,
      seoScore: 0,
      uxScore: 0,
      technicalScore: 0,
      loadingTime: '0s',
      firstContentfulPaint: '0s',
      largestContentfulPaint: '0s',
      criticalIssues: 0,
      warnings: 0,
      analysisDate: new Date().toLocaleDateString('fr-FR')
    },
    loading: true,
    error: null
  });

  const fetchScorePageData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      // Mode test si pas de token
      if (!token) {
        setData(prev => ({
          ...prev,
          overallScore: 68,
          siteUrl: 'https://www.benjie.ch',
          performanceMetrics: {
            globalScore: 83,
            performanceScore: 90,
            seoScore: 67,
            uxScore: 88,
            technicalScore: 100,
            loadingTime: '1.2s',
            firstContentfulPaint: '0.8s',
            largestContentfulPaint: '2.1s',
            criticalIssues: 1,
            warnings: 4,
            analysisDate: new Date().toLocaleDateString('fr-FR')
          },
          loading: false
        }));
        return;
      }

      // Récupérer les données du prospect (incluant l'email pour les demandes d'audit)
      const { data: prospectData, error: prospectError } = await supabase
        .from('prospects')
        .select('id, score, site_url, email')
        .eq('token', token)
        .single();

      if (prospectError) {
        throw new Error(`Erreur prospect: ${prospectError.message}`);
      }

      if (!prospectData) {
        throw new Error('Prospect non trouvé');
      }

      // Récupérer les données d'analyse technique + réponses quiz
      const [instantAnalysis, pagespeedResults, keywordResults, quizResults] = await Promise.all([
        supabase
          .from('instant_page_analysis')
          .select('*')
          .eq('prospect_id', prospectData.id)
          .maybeSingle(),
        supabase
          .from('pagespeed_results')
          .select('*')
          .eq('prospect_id', prospectData.id)
          .maybeSingle(),
        supabase
          .from('keyword_analysis_results')
          .select('*')
          .eq('prospect_id', prospectData.id),
        supabase
          .from('reponses_quiz')
          .select('*')
          .eq('token', token)
      ]);

      const instantData = instantAnalysis.data as InstantPageAnalysisResult | null;
      const pagespeedData = pagespeedResults.data as PageSpeedResult | null;
      const keywordRows = (keywordResults as any)?.data as Array<any> | null;
      const quizAnswers = (quizResults as any)?.data as Array<any> | null;

      // Utilitaires locaux
      function clamp01(n: number) { return Math.max(0, Math.min(1, n)); }
      function pct(n: number) { return Math.round(clamp01(n) * 100); }

      // ===============================================
      // ANALYSER LES RÉPONSES DU QUIZ
      // ===============================================
      interface QuizScores {
        maturity: number;
        acquisition: number;
        innovation: number;
        hasQuiz: boolean;
      }

      const calculateQuizScores = (): QuizScores => {
        if (!quizAnswers || quizAnswers.length === 0) {
          return { maturity: 0, acquisition: 0, innovation: 0, hasQuiz: false };
        }

        // Mapping questions → dimensions
        // Q1: Budget digital → Maturité
        // Q2: Objectif principal → Acquisition
        // Q3: Réseaux sociaux → Marketing
        // Q4: CRM/Automation → Maturité
        // Q5: Analytics → Innovation
        // Q6: Canal acquisition → Acquisition
        // Q7: Budget mensuel → Maturité

        let maturityScore = 0;
        let acquisitionScore = 0;
        let innovationScore = 0;
        let maturityCount = 0;
        let acquisitionCount = 0;
        let innovationCount = 0;

        for (const answer of quizAnswers) {
          const questionId = answer.question_id;
          const scorePartiel = answer.score_partiel || 0;

          // Répartition des questions par dimension
          if (questionId === '1' || questionId === '4' || questionId === '7') {
            // Maturité Digitale (Q1, Q4, Q7)
            maturityScore += scorePartiel;
            maturityCount++;
          } else if (questionId === '2' || questionId === '3' || questionId === '6') {
            // Acquisition & Marketing (Q2, Q3, Q6)
            acquisitionScore += scorePartiel;
            acquisitionCount++;
          } else if (questionId === '5') {
            // Innovation (Q5)
            innovationScore += scorePartiel;
            innovationCount++;
          }
        }

        // Calculer les moyennes (score sur 30 points max par question → convertir en %)
        const maturity = maturityCount > 0 ? (maturityScore / (maturityCount * 30)) * 100 : 0;
        const acquisition = acquisitionCount > 0 ? (acquisitionScore / (acquisitionCount * 30)) * 100 : 0;
        const innovation = innovationCount > 0 ? (innovationScore / (innovationCount * 30)) * 100 : 0;

        return {
          maturity: Math.round(maturity),
          acquisition: Math.round(acquisition),
          innovation: Math.round(innovation),
          hasQuiz: true
        };
      };

      const quizScores = calculateQuizScores();

      // Extraire et agréger les mots-clés (peut être présent sur plusieurs lignes d'analyses)
      const allKeywords: Array<{ keyword: string; position: number; search_volume: number }> = [];
      if (Array.isArray(keywordRows)) {
        for (const row of keywordRows) {
          const arr = Array.isArray(row?.ranked_keywords) ? row.ranked_keywords : [];
          for (const kw of arr) {
            const position = typeof kw?.rank_group === 'number' ? kw.rank_group : (typeof kw?.position === 'number' ? kw.position : 0);
            const vol = typeof kw?.search_volume === 'number' ? kw.search_volume : (typeof kw?.volume === 'number' ? kw.volume : 0);
            if (position > 0 && vol >= 0 && typeof kw?.keyword === 'string') {
              allKeywords.push({ keyword: kw.keyword as string, position, search_volume: vol });
            }
          }
        }
      }

      // Calcul visibilité et part non-brand
      const siteDomain = (prospectData.site_url || '').toLowerCase().replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
      const baseTokens = siteDomain.replace(/\.[a-z]{2,}$/i, '').split(/[-_.]/).filter(Boolean);
      const companyToken = (instantData as any)?.metadata?.company?.toLowerCase?.() || '';
      const companyTokens = companyToken.split(/\s+/).filter(Boolean);
      const brandTokens = new Set<string>([...baseTokens, ...companyTokens].filter(Boolean));

      let totalVol = 0;
      let brandVol = 0;
      let visibilityWeightedVol = 0;
      for (const k of allKeywords) {
        const vol = k.search_volume || 0;
        totalVol += vol;
        const kwLower = k.keyword.toLowerCase();
        const isBrand = Array.from(brandTokens).some(t => t.length >= 3 && kwLower.includes(t));
        if (isBrand) brandVol += vol;
        let w = 0;
        if (k.position <= 3) w = 1.0; else if (k.position <= 10) w = 0.6; else if (k.position <= 20) w = 0.3; else if (k.position <= 100) w = 0.1; else w = 0;
        visibilityWeightedVol += w * vol;
      }
      const visibilityScore = totalVol > 0 ? (visibilityWeightedVol / totalVol) * 100 : 0; // 0-100
      const nonBrandShare = totalVol > 0 ? (1 - (brandVol / totalVol)) : 0; // 0-1

      // Qualité on-page à partir des issues instant_page_analysis
      const critical = (instantData as any)?.seo_issues?.critical || 0;
      const warnings = (instantData as any)?.seo_issues?.warnings || 0;
      const onPagePenalty = Math.min(6, critical) * 8 + Math.min(12, warnings) * 2; // max 48 + 24
      const onPageScore = Math.max(0, 100 - onPagePenalty);

      // Formater les métriques de performance en utilisant les utilitaires
      const performanceMetrics = {
        // Clés alignées avec useScoreData (supporte aussi les anciennes clés *Score ci-dessous)
        performance: convertScoreToPercentage(pagespeedData?.performance_score),
        seo: convertScoreToPercentage(pagespeedData?.seo_score),
        accessibility: convertScoreToPercentage(pagespeedData?.accessibility_score),
        bestPractices: convertScoreToPercentage(pagespeedData?.best_practices_score),
        // Anciennes clés conservées pour compatibilité
        performanceScore: convertScoreToPercentage(pagespeedData?.performance_score),
        seoScore: convertScoreToPercentage(pagespeedData?.seo_score),
        accessibilityScore: convertScoreToPercentage(pagespeedData?.accessibility_score),
        technicalScore: convertScoreToPercentage(pagespeedData?.best_practices_score),
        // Info détaillée
        mobile_performance_score: pagespeedData?.mobile_performance_score ?? null,
        desktop_performance_score: pagespeedData?.desktop_performance_score ?? null,
        loadingTime: formatTimeFromMs(instantData?.page_timing?.duration_time),
        firstContentfulPaint: formatTimeFromMs(instantData?.page_timing?.time_to_interactive),
        largestContentfulPaint: formatTimeFromMs(instantData?.page_timing?.largest_contentful_paint),
        criticalIssues: instantData?.seo_issues?.critical || 0,
        warnings: instantData?.seo_issues?.warnings || 0,
        analysisDate: formatDateToFrench(instantData?.analysis_date),
        globalScore: instantData?.onpage_score || 0,
        // Nouveau: score SEO composite et détails pour transparence
        seoComposite: (() => {
          const pagespeedSeo = convertScoreToPercentage(pagespeedData?.seo_score);
          const comp = 0.50 * visibilityScore + 0.20 * (nonBrandShare * 100) + 0.20 * onPageScore + 0.10 * pagespeedSeo;
          return Math.round(Math.max(0, Math.min(100, comp)));
        })(),
        seoDetails: {
          visibilityScore: Math.round(visibilityScore),
          nonBrandShare: pct(nonBrandShare),
          onPageScore: Math.round(onPageScore),
          pagespeedSeo: convertScoreToPercentage(pagespeedData?.seo_score),
          keywordsAnalyzed: allKeywords.length,
          totalSearchVolume: totalVol
        },
        // NOUVEAU: Scores du quiz pour calcul SmartIndex enrichi
        quizScores: {
          maturity: quizScores.maturity,
          acquisition: quizScores.acquisition,
          innovation: quizScores.innovation,
          hasQuiz: quizScores.hasQuiz
        }
      } as any;

      // Calcul de repli pour overallScore si null en base: pondération 60/40 (mobile/desktop)
      let computedOverall = prospectData.score || 0;
      if (!computedOverall) {
        const m = pagespeedData?.mobile_performance_score;
        const d = pagespeedData?.desktop_performance_score;
        const hasM = typeof m === 'number' && !isNaN(m as any);
        const hasD = typeof d === 'number' && !isNaN(d as any);
        if (hasM || hasD) {
          const wm = hasM ? 0.6 : 0;
          const wd = hasD ? 0.4 : 0;
          const denom = (wm + wd) || 1;
          const value01 = ((hasM ? (m as number) * 0.6 : 0) + (hasD ? (d as number) * 0.4 : 0)) / denom;
          computedOverall = Math.round(value01 * 100);
        }
      }

      setData({
        overallScore: computedOverall,
        siteUrl: prospectData.site_url || '',
        prospectEmail: prospectData.email,
        prospectId: prospectData.id,
        quizCompleted: quizScores.hasQuiz, // Indicateur si quiz complété
        instantPageAnalysis: instantData,
        pagespeedResults: pagespeedData,
        performanceMetrics,
        loading: false,
        error: null
      });

    } catch (error) {
      setData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      }));
    }
  };

  const refreshData = async () => {
    await fetchScorePageData();
  };

  useEffect(() => {
    fetchScorePageData();
  }, [token]);

  return {
    ...data,
    refreshData
  };
}
